import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { servicesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { 
  Check, 
  Calculator, 
  ArrowRight, 
  Sparkles,
  Loader2,
  FileText,
  Recycle,
  ChevronRight,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

// USD to ZMW conversion rate (1 USD = 20 ZMW)
const USD_TO_ZMW = 20;

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadServices();
  }, []);

  // Scroll to section based on hash
  useEffect(() => {
    if (location.hash && !loading) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash, loading]);

  const loadServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = (service) => {
    navigate('/checkout', { state: { service } });
  };

  const handleEstimateVolumeFees = () => {
    navigate('/calculator');
  };

  // Separate services
  const compliancePackagesUnsorted = services.filter(s => !s.is_calculator_based);
  const calculatorService = services.find(s => s.is_calculator_based);
  
  // Sort compliance packages: Standard first, Premium second, Enterprise third
  const compliancePackages = compliancePackagesUnsorted.sort((a, b) => {
    const order = { 'Standard': 1, 'Premium': 2, 'Enterprise': 3 };
    const getOrder = (name) => {
      if (name.includes('Standard')) return order['Standard'];
      if (name.includes('Premium')) return order['Premium'];
      if (name.includes('Enterprise')) return order['Enterprise'];
      return 4;
    };
    return getOrder(a.name) - getOrder(b.name);
  });

  // Format price display
  const formatPrice = (service) => {
    if (service.price_on_request) {
      return 'Price upon request';
    }
    return `$${service.price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="services-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#056839]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="services-page">
      <SEO 
        title="EPR Compliance Services | AfriCycle Zambia Pricing & Packages"
        description="Explore AfriCycle Zambia's EPR compliance packages: Standard, Premium, and Enterprise. Volume-based waste collection and treatment services for plastic packaging and WEEE in Zambia."
        keywords="AfriCycle services, EPR compliance packages Zambia, plastic packaging fees, WEEE compliance pricing, waste collection Zambia, environmental services Africa"
        canonicalUrl="https://africycle.pro/services"
      />
      {/* Page Header */}
      <section className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-[#056839] mb-4">
              HOW OUR SERVICES WORK
            </h1>
            <p className="text-lg text-[#3F3F3F] leading-relaxed">
              A structured approach to plastic packaging and WEEE EPR compliance in Zambia.
            </p>
          </div>
        </div>
      </section>

      {/* Step Flow Indicator */}
      <div className="bg-white py-6 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-[#D2E4D6] rounded-full">
              <span className="w-6 h-6 rounded-full bg-[#056839] text-white text-sm font-semibold flex items-center justify-center">1</span>
              <span className="text-[#056839] font-medium text-sm">Compliance & Regulatory</span>
            </div>
            <ChevronRight className="h-5 w-5 text-[#69A488]" />
            <div className="flex items-center gap-2 px-4 py-2 bg-[#E4D5B7] rounded-full">
              <span className="w-6 h-6 rounded-full bg-[#A0522D] text-white text-sm font-semibold flex items-center justify-center">2</span>
              <span className="text-[#A0522D] font-medium text-sm">Volume-Based Contracting</span>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== STEP 1: Compliance Packages - 3 Column Layout ==================== */}
      <section id="compliance" className="py-16 bg-[#D2E4D6]/30" data-testid="step-1-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge className="bg-[#056839] text-white hover:bg-[#056839] text-sm px-3 py-1">Step 1</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#056839] mb-4">
              COMPLIANCE & REGULATORY SUPPORT
            </h2>
            <p className="text-[#3F3F3F] text-lg max-w-3xl mx-auto">
              We provide regulatory navigation, EPR registration, reporting management, and ongoing 
              compliance consulting to ensure your company meets plastic packaging and WEEE EPR obligations in Zambia.
            </p>
          </div>

          {/* 3-Column Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {compliancePackages.map((service, index) => {
              const isPremium = service.name.includes('Premium');
              const isEnterprise = service.name.includes('Enterprise');
              
              return (
                <div 
                  key={service.id}
                  className={`relative bg-white rounded-2xl shadow-sm border-2 flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    isPremium ? 'border-[#A0522D]' : 'border-gray-100'
                  }`}
                  data-testid={`compliance-card-${index}`}
                >
                  {/* Popular Badge - Only for Premium */}
                  {isPremium && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#A0522D] text-white hover:bg-[#A0522D] px-4 py-1 text-sm shadow-md">
                        <Sparkles className="h-3.5 w-3.5 mr-1" />
                        Most Popular
                      </Badge>
                    </div>
                  )}

                  {/* Card Header */}
                  <div className={`p-6 pb-4 text-center ${isPremium ? 'pt-8' : ''}`}>
                    <h3 className="text-xl font-bold text-[#056839] mb-2">{service.name}</h3>
                    <p className="text-sm text-[#6B7280] mb-4 min-h-[40px]">{service.description}</p>
                    
                    {/* Price */}
                    <div className="mb-4">
                      {service.price_on_request ? (
                        <span className="text-2xl font-bold text-[#056839]">Price upon request</span>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-[#056839]">${service.price.toLocaleString()}</span>
                          <span className="text-[#6B7280] text-sm block mt-1">/year</span>
                          <span className="text-[#A0522D] text-sm block mt-1">
                            ~ZMW {(service.price * USD_TO_ZMW).toLocaleString()}*
                          </span>
                        </>
                      )}
                    </div>

                    {/* CTA Button */}
                    <Button 
                      onClick={() => handleSelectPackage(service)}
                      className={`w-full rounded-full py-3 font-medium transition-all ${
                        isPremium 
                          ? 'bg-[#A0522D] text-white hover:bg-[#8B4726]' 
                          : isEnterprise
                            ? 'bg-[#3F3F3F] text-white hover:bg-[#2F2F2F]'
                            : 'bg-[#056839] text-white hover:bg-[#04522d]'
                      }`}
                      data-testid={`select-package-${index}`}
                    >
                      {service.price_on_request ? 'Contact Us' : 'Select Package'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 mx-6" />

                  {/* Features List - Hide for Enterprise */}
                  {!isEnterprise && (
                    <div className="p-6 pt-4 flex-1">
                      <p className="text-sm font-semibold text-[#056839] mb-4">What's included:</p>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isPremium ? 'bg-[#A0522D]/10' : 'bg-[#D2E4D6]'
                            }`}>
                              <Check className={`h-3 w-3 ${isPremium ? 'text-[#A0522D]' : 'text-[#056839]'}`} />
                            </div>
                            <span className="text-sm text-[#3F3F3F]">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Enterprise - Custom Solutions Text */}
                  {isEnterprise && (
                    <div className="p-6 pt-4 flex-1">
                      <p className="text-sm text-[#6B7280] italic text-center">
                        Contact us for a tailored solution based on your specific needs.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ZMW Disclaimer */}
          <div className="mt-8 bg-[#FEF3C7] border border-[#F59E0B] rounded-xl p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-[#D97706] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#92400E]">
              <span className="font-semibold">*ZMW amounts are indicative only.</span> The actual ZMW amount will be calculated using the prevailing exchange rate at the time of invoicing and will be clearly indicated on your invoice.
            </p>
          </div>
        </div>
      </section>

      {/* ==================== STEP 2: Volume-Based Contracting ==================== */}
      <section id="waste-collection" className="py-16 bg-white" data-testid="step-2-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-4">
              <Badge className="bg-[#A0522D] text-white hover:bg-[#A0522D] text-sm px-3 py-1">Step 2</Badge>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#056839] mb-4">
              VOLUME-BASED WASTE COLLECTION & TREATMENT
            </h2>
            <p className="text-[#3F3F3F] text-lg max-w-3xl mx-auto">
              Once you become a member, contract volume-based certified waste collection and environmentally sound treatment services.
            </p>
          </div>

          {/* Volume-Based Card */}
          {calculatorService && (
            <div 
              className="bg-gradient-to-br from-[#E4D5B7]/20 to-[#E4D5B7]/40 rounded-2xl border-2 border-[#E4D5B7] overflow-hidden"
              data-testid="volume-contract-card"
            >
              <div className="p-8 md:p-10">
                <div className="flex flex-col lg:flex-row lg:items-start gap-8">
                  {/* Left Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-[#A0522D] text-white flex items-center justify-center">
                        <Recycle className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#056839]">{calculatorService.name}</h3>
                        <Badge className="bg-[#A0522D] text-white hover:bg-[#A0522D] text-xs mt-1">
                          Volume-Based Contract
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="text-[#3F3F3F] mb-6">{calculatorService.description}</p>

                    {/* How It Works */}
                    <div className="bg-white rounded-xl p-5 mb-6 border border-[#E4D5B7]">
                      <h4 className="text-sm font-semibold text-[#056839] mb-4">How Volume-Based Pricing Works</h4>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-[#3F3F3F]">
                        <div className="px-4 py-2 bg-[#D2E4D6] rounded-lg font-medium">
                          Material Volume (tonnes)
                        </div>
                        <span className="text-[#69A488] font-bold text-lg">×</span>
                        <div className="px-4 py-2 bg-[#D2E4D6] rounded-lg font-medium">
                          Rate per tonne (USD)
                        </div>
                        <span className="text-[#69A488] font-bold text-lg">=</span>
                        <div className="px-4 py-2 bg-[#056839] text-white rounded-lg font-medium">
                          Your Fee
                        </div>
                      </div>
                    </div>

                    {/* Features Grid */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#056839] mb-4">Included Services</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {calculatorService.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <div className="w-5 h-5 rounded-full bg-[#A0522D]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="h-3 w-3 text-[#A0522D]" />
                            </div>
                            <span className="text-sm text-[#3F3F3F]">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right CTA Panel */}
                  <div className="lg:w-80 flex-shrink-0">
                    <div className="bg-white rounded-xl p-6 border border-[#E4D5B7] shadow-sm">
                      <div className="text-center mb-6">
                        <div className="w-14 h-14 rounded-full bg-[#056839] text-white flex items-center justify-center mx-auto mb-4">
                          <Calculator className="h-7 w-7" />
                        </div>
                        <h4 className="font-semibold text-[#056839] mb-2">Calculate Your Fees</h4>
                        <p className="text-sm text-[#6B7280]">
                          Get an instant estimate
                        </p>
                      </div>
                      <Button 
                        onClick={handleEstimateVolumeFees}
                        className="w-full bg-[#056839] text-white hover:bg-[#04522d] rounded-full py-3 font-medium"
                        data-testid="estimate-volume-fees-btn"
                      >
                        Estimate Volume Fees
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      <p className="text-xs text-[#A0522D] text-center mt-4">
                        Available after completing Step 1 membership
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ==================== Bottom CTA ==================== */}
      <section className="py-12 bg-[#F9FAFB] border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-semibold text-[#056839] mb-3">Not sure which package to choose?</h3>
          <p className="text-[#6B7280] mb-6">
            Reach out to us and we'll help you determine the right solution for your business.
          </p>
          <Button 
            onClick={() => navigate('/contact')}
            className="bg-[#056839] text-white hover:bg-[#04522d] rounded-full px-8 py-3 font-medium"
            data-testid="contact-us-btn"
          >
            Contact Us
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
